import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {PrimeNgImportsModule} from '../../prime-ng-imports';
import {IBaseComponent} from '../ibase/ibase.component';
import {
    ETableColDataType,
    ETableColFilterType,
    ETableColFreeze,
    ETableColType,
    TableColDef,
    TableTagDef
} from './table-col-def';
import {TableColDefCache} from './table-col-def-cache';
import {
    QueryPaginatorService,
    ROWS_PER_PAGE_OPTIONS,
    SDKQueryPaginatorConfig
} from '../../services/query-paginator.service';
import {TableContextMenuSelectEvent, TableLazyLoadEvent, TableRowReorderEvent} from 'primeng/table';
import {MenuItem, SortEvent} from 'primeng/api';
import {get} from 'lodash';
import {Subscription} from 'rxjs';
import {EStatusTagDisplayMode, StatusTagComponent} from '../status-tag/status-tag.component';
import {SdkBridgeBaseModel} from '../../../../src/app/sdk-bridge';
import {AutoRefreshComponent} from '../auto-refresh/auto-refresh.component';
import {AutoRefreshInterval} from '../../services/auto-refresh.service';
import {NgComponentOutlet} from '@angular/common';

@Component({
    selector: 'app-generic-table',
    templateUrl: './generic-table.component.html',
    styleUrls: ['./generic-table.component.scss'],
    imports: [PrimeNgImportsModule, StatusTagComponent, AutoRefreshComponent, NgComponentOutlet]
})
export class GenericTableComponent<t_RowType> extends IBaseComponent {
    protected readonly get = get;
    protected readonly ETableColType = ETableColType;
    protected readonly ETableColDataType = ETableColDataType;
    protected readonly ETableColFilterType = ETableColFilterType;
    protected readonly ETableColFreeze = ETableColFreeze;
    protected readonly EStatusTagDisplayMode = EStatusTagDisplayMode;
    protected readonly ROWS_PER_PAGE_OPTIONS = ROWS_PER_PAGE_OPTIONS;

    // ===============================================================
    // ===============================================================

    private static _tableIDGen = 1;

    // ===============================================================
    // ===============================================================

    private _paginatorService: QueryPaginatorService = inject(QueryPaginatorService);
    private _paginatorServiceSubscription?: Subscription;
    private _refreshTimeout: number | undefined;
    private _isAlreadyInitDefaultValue: Record<string, boolean> = {};

    // ===============================================================
    // ===============================================================

    protected _tableID: string;
    protected _tableColIDGen = 1;
    protected _paginatorConfig: SDKQueryPaginatorConfig = {};

    protected _buttonsColWidth = '5rem';

    protected _searchValue?: string;
    protected _cols: TableColDef[] = [];
    protected _selectedColumns: TableColDef[] = [];
    protected _colsCache: Record<number, TableColDefCache> = {};
    protected _dataCols: TableColDef[] = [];
    protected _buttonCols: TableColDef[] = [];
    protected _subRowColumn?: TableColDef;
    protected rowWrapperClassList = 'default-cursor';
    protected searcheableFields: string[] = [];
    protected filterableFields: string[] = [];
    protected rowFilters: string[] = [];
    protected expandedRowKeys: Record<string, boolean> = {};
    protected _contextMenuSelection: unknown;
    protected _contextMenu?: MenuItem[];
    protected showFilters = false;
    protected showColumnChooser = false;

    // ===============================================================
    // ===============================================================

    @Input()
    public data: t_RowType[] | SdkBridgeBaseModel[] = [];
    public get dataAsRowData(): object[] {
        return this.data as object[];
    }

    @Input()
    public totalCount = -1;

    public get cols(): TableColDef[] {
        return this._cols;
    }
    @Input()
    public set cols(values: TableColDef[]) {
        this._cols = values;
        this.refreshTableColsCache();
    }

    public get selectedColumns(): TableColDef[] {
        return this._selectedColumns;
    }
    @Input()
    public set selectedColumns(values: TableColDef[]) {
        this._selectedColumns = this.cols.filter((col) => values.includes(col));
    }

    public get contextMenu(): MenuItem[] | undefined {
        return this._contextMenu;
    }
    @Input()
    public set contextMenu(value: MenuItem[] | undefined) {
        this._contextMenu = value;
        this.computeButtonColumnWidth();
    }

    public get paginatorConfig(): SDKQueryPaginatorConfig {
        return this._paginatorConfig;
    }

    @Input() public entityName = 'Model';
    @Input() public dataKey = 'uuid';
    @Input() public isRootTable = true;
    @Input() public flexHeight = false;
    @Input() public scrollHeight?: string;

    @Input() public showTopBar = true;
    @Input() public showColumnToggle = true;
    @Input() public showSearch = true;
    @Input() public showRefreshButton = true;
    @Input() public showHelpButton = false;
    @Input() public showNewButton = true;
    @Input() public showFilterButton = true;
    @Input() public newButtonCaption?: string;
    @Input() public showAutoRefresh = false;
    @Input() public autoRefreshDefaultInterval = AutoRefreshInterval.OFF;

    @Input() public showNonRootTopBar = false;
    @Input() public showNonRootSearch = false;
    @Input() public showNonRootPaginator = false;
    @Input() public showNonRootBasicPaginator = false;

    @Input() public updateBrowerURL = true;

    @Input() public showTableLoading = false;
    @Input() public showTableError = false;
    @Input() public tableLoadingStr = 'Loading';

    @Input() public onRowReorder?: (event: TableRowReorderEvent) => void;

    @Input() public showPaginator = true;
    @Input() public showBasicPaginator = false;

    @Input() public defaultPaginatorConfig?: SDKQueryPaginatorConfig;

    @Output() public readyEvent = new EventEmitter<void>();
    @Output() public refreshEvent = new EventEmitter<SDKQueryPaginatorConfig>();
    @Output() public helpEvent = new EventEmitter<void>();
    @Output() public newEvent = new EventEmitter<void>();
    @Output() public contextMenuSelectEvent = new EventEmitter<unknown>();

    @Input() sortFn?: (event: SortEvent) => t_RowType[];
    @Input() pushDataOnFly = false;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this._tableID = 'GenericTable' + GenericTableComponent._tableIDGen.toString();
        GenericTableComponent._tableIDGen++;

        if (this.showPaginator) {
            this._paginatorConfig = {
                ...this._paginatorService.initRestorePaginator(this.defaultPaginatorConfig, this.updateBrowerURL)
            };
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        if (this.showPaginator) {
            this._paginatorConfig = {
                ...this._paginatorService.initRestorePaginator(this.defaultPaginatorConfig, this.updateBrowerURL)
            };
        }

        this.refreshTableColsCache();

        if (!this.showPaginator) {
            this.rowFilters = [];
        }

        this.readyEvent?.emit();
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        if (this._paginatorServiceSubscription) this._paginatorServiceSubscription.unsubscribe();
    }

    // ===============================================================
    // ===============================================================

    /**
     * On lazy load event / filtering
     * @param event Data
     */
    protected onPageChange(event: TableLazyLoadEvent) {
        const filterRows: Record<string, string | number | boolean> = {};

        Object.entries(event.filters ?? {}).map(([key, value]) => {
            if (
                value !== undefined &&
                value !== null &&
                !Array.isArray(value) &&
                value.value !== undefined &&
                value.value !== null &&
                (typeof value.value !== 'string' || value.value.length > 0)
            ) {
                filterRows[key] = value.value;
            }
        });

        let sortField: string | undefined = undefined;
        if (event.sortField === null || event.sortField === undefined) sortField = undefined;
        else if (Array.isArray(event.sortField)) sortField = event.sortField.join(',');
        else sortField = event.sortField;

        let filter: string | undefined = undefined;
        if (event.globalFilter === null || event.globalFilter === undefined) filter = undefined;
        else if (Array.isArray(event.globalFilter)) filter = event.globalFilter.join(',');
        else filter = event.globalFilter;

        const paginatorUpdateResult = this._paginatorService.updatePaginatorConfig(
            {
                limit: event.rows !== null ? event.rows : undefined,
                offset: event.first,
                sort: sortField,
                order: event.sortOrder !== null ? event.sortOrder : undefined,
                filter: filter,
                cols_to_search: this.searcheableFields,
                filters_rows: filterRows
            },
            this.defaultPaginatorConfig,
            this.updateBrowerURL
        );

        if (paginatorUpdateResult.updated) {
            this._paginatorConfig = paginatorUpdateResult.paginatorConfig;

            this.showTableLoading = true;
            clearTimeout(this._refreshTimeout);
            this._refreshTimeout = setTimeout(() => {
                this.refreshEvent.emit();
                this.showTableLoading = false;
            }, 300);
        }
    }
    /**
     * On simple page change
     */
    protected onSimplePageChange() {
        this.showTableLoading = true;
        setTimeout(() => {
            this.showTableLoading = false;
        }, 300);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Refresh the table cols cache
     */
    private refreshTableColsCache() {
        this._colsCache = {};
        this.cols.forEach((x) => {
            if (!x.__iID) {
                x.__iID = this._tableColIDGen++;
            }
            const cache = new TableColDefCache();
            cache._init(x.__iID, this.cols, x);
            this._colsCache[x.__iID] = cache;
        });

        this.searcheableFields.splice(0);
        this.filterableFields.splice(0);

        this.cols.forEach((col) => {
            if (col.type !== ETableColType.DATA) {
                return;
            }

            if (col.searcheable === true) this.searcheableFields.push(col.field);
            if (col.filterable === true) this.filterableFields.push(col.field);
        });

        if (this.searcheableFields.length === 0) {
            this.showSearch = false;
        }

        this._dataCols.splice(0);
        this._dataCols.push(...this._cols.filter((x) => x.type === ETableColType.DATA));

        this._buttonCols.splice(0);
        this._buttonCols.push(...this._cols.filter((x) => x.type === ETableColType.BUTTON));

        this.computeButtonColumnWidth();

        this.selectedColumns.splice(0);
        this.selectedColumns.push(...this._cols.filter((x) => x.type === ETableColType.DATA));

        this._subRowColumn = this._cols.find(
            (x) => x.fnChildrenRows && this._colsCache[x.__iID!].conditionEval[this.isRootTable ? 1 : 0]
        );
    }
    /**
     * Compute the button column width
     */
    private computeButtonColumnWidth() {
        const buttonCount = this._buttonCols.length + (this._contextMenu ? 1 : 0);

        const paddingX = 2 * 0.5; // Left + Right padding of the cell
        const buttonW = 2; // Width of a button
        const buttonGapW = 2; // Width of the gap between two button

        this._buttonsColWidth = paddingX + buttonCount * buttonW + (buttonCount - 1) * buttonGapW + 'rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get the value of the filter row for the given field
     * @param field The field to get the value of
     * @param isMultiselect If true, the value is splitted by '|' because multiple can have multiple values
     */
    protected getFilterRowValue(field: string): string | string[] | number | boolean | null | undefined {
        const value = this._paginatorConfig.filters_rows?.[field] ?? undefined;

        if (value) {
            if (value === 'true') {
                return true;
            } else if (value === 'false') {
                return false;
            } else if (value === 'null') {
                return null;
            } else {
                return value;
            }
        }
        return undefined;
    }
    /**
     * Get the tag of the filter row for the given field and value (used for multiselect and dropdown rowFilter)
     */
    protected getFilterRowTagOpt(col: TableColDef, label: string): TableTagDef | undefined {
        return col.tags?.find((opt) => opt.value === label) ?? undefined;
    }
    /**
     * Get the default value and set it if it exists only the first time
     * @protected
     */
    protected getDefaultValue(col: TableColDef, filter: (value: string) => void): string | null {
        if (col.filterSelectDefaultOption && filter && !this._isAlreadyInitDefaultValue[col.field]) {
            this._isAlreadyInitDefaultValue[col.field] = true;
            filter(col.filterSelectDefaultOption);
            return col.filterSelectDefaultOption;
        }
        return null;
    }
    protected applyFilterOnKeyDown($event: Event, filter: (value: string) => void) {
        if (!$event || !$event.target) {
            filter('');
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        filter(($event.target as any).value ?? '');
    }

    /**
     * Get the children of the given row in the cols.children function
     * @protected
     */
    getChildrenInCols(row: object) {
        return this.cols.find((col) => col.fnChildrenRows)?.fnChildrenRows?.(row) || [];
    }

    // ===============================================================
    // ===============================================================

    /**
     * Convert the $event which is an array of date to a string with the format 'MM/DD/YYYY | MM/DD/YYYY'
     * @param $event The event emitted by the calendar (array of date)
     * @param filter The filter function to set the value
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected convertIntervalDate($event: any, filter: any) {
        if ($event?.length === 2 && $event[0] && $event[1]) {
            filter(`${$event[0]?.toLocaleDateString() || ''} | ${$event[1]?.toLocaleDateString() || ''}`);
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the context menu is being show for a node
     */
    protected contextMenuSelect(data: TableContextMenuSelectEvent<unknown>) {
        this.contextMenuSelectEvent.next(data.data!);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Show or hide filters
     */
    protected onFilterClick(): void {
        this.showFilters = !this.showFilters;
        this.showColumnChooser = !this.showColumnChooser;
    }
}
